import {
  LOGISTICS_CALCULATION_SHEET_CHROME,
  calculateLogisticsCalculationPayable,
  formatSheetAmount,
  parseDocumentDecimal,
  type LogisticsCalculationCurrency,
  type LogisticsCalculationMoneyTotals,
  type LogisticsCalculationPublicDto,
} from '@magimp/shared';

const chrome = LOGISTICS_CALCULATION_SHEET_CHROME.document;

/**
 * The document prints amounts and dates the way the reference sheet does, so
 * the locale is the document's own, not the operator's UI language.
 */
export const DOCUMENT_LOCALE = 'ru-RU';

// WHY: density is mass-unit per volume-unit by definition, so the two cargo
// units the measures column needs are exactly the two halves of the shared
// `densityUnit` constant. Deriving them keeps the units in the frozen customer
// chrome instead of duplicating them as web literals the i18n gate would fail.
const [weightUnit = '', volumeUnit = ''] = chrome.densityUnit.split('/');
export const DOCUMENT_WEIGHT_UNIT = weightUnit;
export const DOCUMENT_VOLUME_UNIT = volumeUnit;

/** One printed money value, kept beside its currency so a row can key on it. */
export interface PrintedAmount {
  currency: LogisticsCalculationCurrency;
  text: string;
}

/** True for a currency total that is exactly nothing, which is never an amount due. */
export function isZeroAmount(amount: string | null | undefined): boolean {
  return amount === null || amount === undefined || Number.parseFloat(amount) === 0;
}

/** Every non-zero currency of a frozen totals triple, in document order. */
export function nonZeroAmounts(
  totals: LogisticsCalculationMoneyTotals | null | undefined,
): PrintedAmount[] {
  if (!totals) return [];
  const parts: Array<[LogisticsCalculationCurrency, string]> = [
    ['USD', totals.usd],
    ['CNY', totals.cny],
    ['RUB', totals.rub],
  ];
  return parts
    .filter(([, amount]) => !isZeroAmount(amount))
    .map(([currency, amount]) => ({ currency, text: formatSheetAmount(amount, currency) }));
}

export type DocumentRates = Pick<
  LogisticsCalculationPublicDto,
  'cnyPerUsdRate' | 'rubPerUsdRate' | 'rubPerCnyRate'
>;

/**
 * What a per-currency total comes to in the currencies the customer pays:
 * `= $1 418,89 + 14 500 ₽`, the yuan folded into dollars at the document rate
 * and the roubles left as they are. The same arithmetic as the split settlement
 * in the foot, so the two never disagree.
 *
 * Null when the total holds no yuan — the line would repeat the figures above
 * it — or when the document states no rate to fold them at.
 */
export function settlementLine(
  totals: LogisticsCalculationMoneyTotals | null | undefined,
  rates: DocumentRates | null | undefined,
): string | null {
  if (!totals || !rates?.rubPerUsdRate || isZeroAmount(totals.cny)) return null;
  if (!rates.cnyPerUsdRate && !rates.rubPerCnyRate) return null;
  const { splitUsd, splitRub } = calculateLogisticsCalculationPayable(
    totals,
    {
      rubPerUsdRate: rates.rubPerUsdRate,
      cnyPerUsdRate: rates.cnyPerUsdRate,
      rubPerCnyRate: rates.rubPerCnyRate,
    },
    undefined,
  );
  const parts = [formatSheetAmount(splitUsd, 'USD')];
  if (!isZeroAmount(splitRub)) parts.push(formatSheetAmount(splitRub, 'RUB'));
  return `= ${parts.join(' + ')}`;
}

/** `2026-09-08` as the `08.09.2026` the masthead prints beside the code. */
export function formatSheetDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat(DOCUMENT_LOCALE, {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
}

/** An ISO instant as the `28.08` the cargo row stacks above its arrival. */
export function formatCargoDayMonth(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat(DOCUMENT_LOCALE, { day: '2-digit', month: '2-digit' }).format(
    parsed,
  );
}

/**
 * Cargo density, or null when either measure is free text or the volume is
 * nothing. INVARIANT: a dirty registry string is never guessed at — the cell
 * drops the figure rather than printing a fabricated one.
 */
export function cargoDensity(
  weight: string | null | undefined,
  volume: string | null | undefined,
): string | null {
  const mass = measureOf(weight);
  const cubic = measureOf(volume);
  if (mass === null || cubic === null || cubic === 0) return null;
  return `${formatDecimal(mass / cubic, 1)} ${chrome.densityUnit}`;
}

/**
 * The rouble price of one yuan as a machine decimal, so the masthead can print
 * it through the same formatter every other amount goes through.
 */
export function rubPerCnyRate(
  rubPerUsdRate: string | null | undefined,
  cnyPerUsdRate: string | null | undefined,
): string | null {
  const rub = measureOf(rubPerUsdRate);
  const cny = measureOf(cnyPerUsdRate);
  if (rub === null || cny === null || cny === 0) return null;
  return (rub / cny).toFixed(2);
}

/**
 * A measure the customer reads.
 *
 * INVARIANT: only a clean decimal is formatted and given its unit. A registry
 * value the manager left as prose is omitted because the document cannot state
 * it as a measured fact.
 */
export function measureWithUnit(value: string | null | undefined, unit: string): string | null {
  const parsed = measureOf(value);
  return parsed === null ? null : `${formatDecimal(parsed, 3)} ${unit}`;
}

/**
 * The totals row's own measure, or null when any cargo's value for it is not a
 * clean decimal — a column that cannot be added up is left out rather than
 * stated as a partial sum.
 */
export function sumMeasures(values: ReadonlyArray<string | null | undefined>): number | null {
  if (values.length === 0) return null;
  let total = 0;
  for (const value of values) {
    const parsed = measureOf(value);
    if (parsed === null) return null;
    total += parsed;
  }
  return total;
}

export function formatDecimal(value: number, maximumFractionDigits: number): string {
  return new Intl.NumberFormat(DOCUMENT_LOCALE, { maximumFractionDigits }).format(value);
}

function measureOf(value: string | null | undefined): number | null {
  return value === null || value === undefined || value === '' ? null : parseDocumentDecimal(value);
}
